import { defineStore } from 'pinia'
import api from '@/modules/account/api'
import { useAccountStore } from '@/modules/account/store/accountStore'
import { OrderStatus } from '@bigbank/dc-common/clients/http/account/modules/pending-payments/pending-payment.types'
import { useRootStore } from '@/store/root'
import { PendingPaymentPageNumber, PendingPaymentPageSize } from '@/modules/account/const'

export const usePendingPaymentStore = defineStore('pendingPayment', {
  state: () => ({
    pendingPayments: [],
    isPendingPaymentsLoading: false,
    selectedIban: null,
    pageSize: PendingPaymentPageSize,
    pageNumber: PendingPaymentPageNumber
  }),
  actions: {
    async searchPendingPayments (fromIban = null) {
      this.isPendingPaymentsLoading = true

      try {
        if (!this.selectedIban) {
          const accountStore = useAccountStore()
          await accountStore.getAccountsForPayment()
          const selectedIban = fromIban || accountStore.paymentAccounts[0].iban
          this.setSelectedIban(selectedIban)
        }

        const response = await api.searchPendingPayments({
          pageSize: this.pageSize,
          pageNumber: this.pageNumber,
          fromIban: [this.selectedIban]
        })
        this.pendingPayments = response.values
      } catch (err) {
        this.$apm?.captureError(err)
      } finally {
        this.isPendingPaymentsLoading = false
      }
    },
    async searchAllAccounts () {
      this.isPendingPaymentsLoading = true

      try {
        const accountStore = useAccountStore()
        await accountStore.getAccountsForPayment()
        const fromIban = accountStore.paymentAccounts.map(account => account.iban)

        const response = await api.searchPendingPayments({
          pageSize: this.pageSize,
          pageNumber: this.pageNumber,
          fromIban
        })
        return response.values
      } catch (err) {
        this.$apm?.captureError(err)
      } finally {
        this.isPendingPaymentsLoading = false
      }
    },
    async createPendingPayment (body) {
      try {
        await api.createPendingPayment(body)
        await this.setSelectedIban(body.fromIban)
        await this.reloadPendingPayments()
      } catch (err) {
        this.$apm?.captureError(err)
        throw err
      }
    },
    async modifyPendingPayment (id, body) {
      try {
        await api.modifyPendingPayment(id, body)
        await this.reloadPendingPayments()
      } catch (err) {
        this.$apm?.captureError(err)
        throw err
      }
    },
    async cancelPendingPayments (ids) {
      this.isPendingPaymentsLoading = true
      try {
        await api.cancelPendingPayments({ pendingPaymentIds: ids })
        await this.reloadPendingPayments()
      } catch (err) {
        this.$apm?.captureError(err)
        throw err
      } finally {
        this.isPendingPaymentsLoading = false
      }
    },
    setSelectedIban (iban) {
      this.selectedIban = iban
    },
    async reloadPendingPayments () {
      await this.searchPendingPayments()
      const rootStore = useRootStore()
      // Deliberately not using await here to avoid blocking the UI
      rootStore.getPaymentCount(true)
    },
    async fetchPendingById (pendingPaymentId) {
      const getPending = (pendings, id) => pendings.find(payment => payment.id === Number(id))
      const payment = getPending(this.pendingPayments, pendingPaymentId)
      if (payment?.id) return payment

      // Fallback if payment form is navigated to directly and data is not loaded
      const pendings = await this.searchAllAccounts()
      const fallBackPending = getPending(pendings, pendingPaymentId)
      if (fallBackPending?.id) return fallBackPending
      return {}
    }
  },
  getters: {
    waitingForConfirmation () {
      return this.pendingPayments.filter(payment => payment.orderStatus === OrderStatus.NotConfirmed)
    }
  }
})
